import { useQuery } from "@tanstack/react-query";
import { Alert, Spin } from "antd";
import type { ReactNode } from "react";

import { ApiClientError } from "@/api/api_client";
import {
  load_project_customization,
} from "@/customization/project_customization";
import { project_customization_context } from "@/customization/project_customization_context";
import { web_bootstrap_context } from "@/app/web_bootstrap_context";
import { get_web_bootstrap } from "@/features/bootstrap/bootstrap_api";

interface BootstrapBoundaryProps {
  children: ReactNode;
}

export function BootstrapBoundary({ children }: BootstrapBoundaryProps) {
  const bootstrap_query = useQuery({
    queryKey: ["web-bootstrap"],
    queryFn: get_web_bootstrap,
    refetchOnMount: "always",
  });
  const user_mark = bootstrap_query.data?.data.customization.user_mark;
  const customization_query = useQuery({
    queryKey: ["project-customization", user_mark ?? ""],
    queryFn: () => load_project_customization(user_mark),
    enabled: bootstrap_query.data !== undefined,
  });

  if (
    bootstrap_query.isPending ||
    bootstrap_query.isFetching ||
    customization_query.isPending
  ) {
    return <Spin fullscreen tip="正在进入系统" />;
  }

  const error = bootstrap_query.error ?? customization_query.error;
  if (error) {
    const message =
      error instanceof ApiClientError
        ? error.message
        : error instanceof Error
          ? error.message
          : "系统启动失败，请刷新后重试";
    return (
      <Alert
        type="error"
        showIcon
        title="系统启动失败"
        description={message}
      />
    );
  }

  if (!bootstrap_query.data || !customization_query.data) {
    return (
      <Alert
        type="error"
        showIcon
        title="系统启动失败"
        description="项目定制脚本未返回有效结果"
      />
    );
  }

  return (
    <web_bootstrap_context.Provider value={bootstrap_query.data}>
      <project_customization_context.Provider value={customization_query.data}>
        {children}
      </project_customization_context.Provider>
    </web_bootstrap_context.Provider>
  );
}
